import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STRINGS = {
    en: {
        nav: {
            problem: "The Problem",
            solution: "Solution",
            who: "Who it's for",
            roadmap: "Roadmap",
            signin: "Sign in",
            waitlist: "Join the waitlist",
            dashboard: "Dashboard",
            signout: "Sign out",
        },
        hero: {
            eyebrow: "Commerce infrastructure for Africa",
            line1: "One app.",
            line2: "Every opportunity.",
            sub: "OneGo is the merchant-first operating system for Africa's informal economy — wallet, digital services, procurement, credit and logistics in a single mobile-first platform.",
            cta: "Open a merchant account",
            cta2: "Explore the platform",
            mobile: "Mobile-first",
            offline: "Offline resilient",
            agents: "Built for super-agents",
        },
        ussd: {
            chip: "Low-bandwidth access",
            title: "USSD and WhatsApp for every phone.",
            body: "OneGo merchants without smartphones can sell, top up and check balance via *483*OG# (sandbox) or chat with the OneGo bot on WhatsApp.",
            ussd_label: "Dial",
            whatsapp_label: "WhatsApp",
        },
    },
    sw: {
        nav: {
            problem: "Tatizo",
            solution: "Suluhu",
            who: "Ni ya nani",
            roadmap: "Ratiba",
            signin: "Ingia",
            waitlist: "Jisajili",
            dashboard: "Dashibodi",
            signout: "Toka",
        },
        hero: {
            eyebrow: "Miundombinu ya biashara Afrika",
            line1: "App moja.",
            line2: "Kila fursa.",
            sub: "OneGo ni mfumo wa uendeshaji wa wafanyabiashara katika uchumi usio rasmi wa Afrika — pochi, huduma za kidijitali, ununuzi, mikopo na usafirishaji katika jukwaa moja la simu.",
            cta: "Fungua akaunti ya mfanyabiashara",
            cta2: "Chunguza jukwaa",
            mobile: "Simu kwanza",
            offline: "Inayostahimili nje ya mtandao",
            agents: "Imejengwa kwa super-agents",
        },
        ussd: {
            chip: "Ufikiaji wa kasi ya chini",
            title: "USSD na WhatsApp kwa kila simu.",
            body: "Wafanyabiashara wa OneGo wasio na simu janja wanaweza kuuza, kuongeza na kuangalia salio kupitia *483*OG# (sandbox) au kuongea na bot ya OneGo kwenye WhatsApp.",
            ussd_label: "Piga",
            whatsapp_label: "WhatsApp",
        },
    },
    fr: {
        nav: {
            problem: "Le Problème",
            solution: "Solution",
            who: "Pour qui",
            roadmap: "Feuille de route",
            signin: "Connexion",
            waitlist: "Liste d'attente",
            dashboard: "Tableau de bord",
            signout: "Déconnexion",
        },
        hero: {
            eyebrow: "Infrastructure de commerce pour l'Afrique",
            line1: "Une app.",
            line2: "Toutes les opportunités.",
            sub: "OneGo est le système d'exploitation des marchands de l'économie informelle africaine — portefeuille, services digitaux, approvisionnement, crédit et logistique dans une seule plateforme mobile-first.",
            cta: "Ouvrir un compte marchand",
            cta2: "Découvrir la plateforme",
            mobile: "Mobile d'abord",
            offline: "Résilient hors-ligne",
            agents: "Conçu pour les super-agents",
        },
        ussd: {
            chip: "Accès à faible bande passante",
            title: "USSD et WhatsApp pour chaque téléphone.",
            body: "Les marchands OneGo sans smartphone peuvent vendre, recharger et consulter le solde via *483*OG# (sandbox) ou discuter avec le bot OneGo sur WhatsApp.",
            ussd_label: "Composer",
            whatsapp_label: "WhatsApp",
        },
    },
};

const LANGS = [
    { code: "en", label: "EN", name: "English" },
    { code: "sw", label: "SW", name: "Swahili" },
    { code: "fr", label: "FR", name: "Français" },
];

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("onego_lang") || "en");
    useEffect(() => {
        localStorage.setItem("onego_lang", lang);
    }, [lang]);

    const t = useMemo(() => STRINGS[lang] || STRINGS.en, [lang]);

    return (
        <I18nCtx.Provider value={{ lang, setLang, t, langs: LANGS }}>{children}</I18nCtx.Provider>
    );
}

export function useI18n() {
    const ctx = useContext(I18nCtx);
    if (!ctx) throw new Error("useI18n must be used within I18nProvider");
    return ctx;
}
